// from https://github.com/tines/tines/blob/main/frontend/src/components/Workbench/ExistingConversation/useChatScroll.ts

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

// a few px of slack: mobile browsers report fractional scrollTop
const SCROLL_TOLERANCE = 4;
const scrolledToBottom = (c: HTMLElement | null) =>
  c &&
  Math.abs(c.scrollHeight - c.scrollTop - c.clientHeight) < SCROLL_TOLERANCE;

const scrollToBottom = (c: HTMLElement | null) => {
  if (!c) return;
  c.scrollTo({ top: c.scrollHeight });
};

// for https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
const allChanges = {
  characterData: true,
  attributes: true,
  childList: true,
  subtree: true,
};

export default (
  externalRef?: React.RefObject<HTMLDivElement | null>,
  paused = false
): [React.RefObject<HTMLDivElement | null>, () => void, boolean] => {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const scrollContainer = externalRef ?? internalRef;
  const [userControlsScroll, setUserControlsScroll] = useState(false);
  const userControlsRef = useRef(false);
  // Suspends autoscroll while a touch is in progress without showing the
  // scroll-to-bottom button: a tap on a fully-visible chat must not flash it.
  const touchActiveRef = useRef(false);

  const canFollow = useEffectEvent(
    () => !paused && !touchActiveRef.current && !userControlsRef.current
  );

  const resetScroll = useCallback(() => {
    requestAnimationFrame(() => {
      userControlsRef.current = false;
      setUserControlsScroll(false);
      scrollToBottom(scrollContainer.current);
    });
  }, [scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;

    const setUserControls = (requestedControl: boolean) => {
      // A pull at the top must not suspend autoscroll or show the latest button
      // when the whole conversation already fits in the viewport.
      const value =
        requestedControl &&
        container.scrollHeight - container.clientHeight >= SCROLL_TOLERANCE;
      if (userControlsRef.current !== value) {
        userControlsRef.current = value;
        setUserControlsScroll(value);
      }
    };

    let previousScrollTop = container.scrollTop;
    let previousScrollHeight = container.scrollHeight;
    const onUserScroll = () =>
      setUserControls(userControlsRef.current || !scrolledToBottom(container));
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY === 0) return;
      previousScrollTop = container.scrollTop;
      previousScrollHeight = container.scrollHeight;
      setUserControls(event.deltaY < 0 || !scrolledToBottom(container));
    };

    // Autoscroll is suspended for the whole touch (via touchActiveRef in the
    // observers): a fling from the bottom still reads at-bottom when the
    // finger lifts, and any programmatic scrollTo cancels iOS momentum.
    const onTouchStart = () => {
      previousScrollTop = container.scrollTop;
      previousScrollHeight = container.scrollHeight;
      touchActiveRef.current = true;
    };
    const onTouchEnd = () => {
      touchActiveRef.current = false;
      // Keep momentum covered after the finger lifts: if the fling already
      // moved away from the bottom, hand control to the user before the
      // observers resume.
      onUserScroll();
    };

    // The bottom tolerance must not recapture a gentle upward gesture.
    const onScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const scrolledUp = scrollTop < previousScrollTop;
      const scrolledDown = scrollTop > previousScrollTop;
      const clampedToBottom =
        scrollHeight < previousScrollHeight &&
        scrollTop >= scrollHeight - container.clientHeight;
      previousScrollTop = scrollTop;
      previousScrollHeight = scrollHeight;
      if (clampedToBottom) {
        setUserControls(false);
      } else if (touchActiveRef.current && scrolledUp) {
        setUserControls(true);
      } else if (
        userControlsRef.current &&
        scrolledDown &&
        scrolledToBottom(container)
      ) {
        setUserControls(false);
      }
    };

    container.addEventListener("wheel", onWheel, { passive: true });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onUserScroll, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });
    container.addEventListener("scrollend", onUserScroll, { passive: true });
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onUserScroll);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      container.removeEventListener("scrollend", onUserScroll);
      container.removeEventListener("scroll", onScroll);
    };
  }, [scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container || userControlsScroll) return;
    const observer = new MutationObserver(() => {
      if (canFollow()) scrollToBottom(container);
    });
    observer.observe(container, allChanges);
    return () => observer.disconnect();
  }, [userControlsScroll, scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container || userControlsScroll) return;
    const observer = new ResizeObserver(() => {
      if (canFollow()) scrollToBottom(container);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [userControlsScroll, scrollContainer]);

  return [scrollContainer, resetScroll, userControlsScroll];
};
